import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';

/**
 * Minimal user info stored for recent users
 */
export interface RecentUserEntry {
  id: string;
  name: string;
  sectionId: string;
  sex?: string; // "male" | "female" | undefined
  accessedAt: number; // Unix timestamp (ms)
}

const STORAGE_KEY = 'gokuri-recent-users';
const MAX_ENTRIES = 10;
const DEFAULT_RETENTION_DAYS = 7;

@Injectable({ providedIn: 'root' })
export class RecentUsersService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Retention period in days - can be changed at runtime */
  private readonly _retentionDays = signal(DEFAULT_RETENTION_DAYS);

  /** Internal state combining session + persistent storage */
  private readonly _recentUsers = signal<RecentUserEntry[]>(this.loadFromStorage());

  /** Public readonly signal for recent users */
  readonly recentUsers = this._recentUsers.asReadonly();

  /** Retention days (public readonly) */
  readonly retentionDays = this._retentionDays.asReadonly();

  /** Filtered list excluding expired entries */
  readonly validRecentUsers = computed(() => {
    const cutoff = Date.now() - this._retentionDays() * 24 * 60 * 60 * 1000;
    return this._recentUsers().filter(u => u.accessedAt >= cutoff);
  });

  /**
   * Get recent users filtered by section
   */
  getRecentUsersForSection(sectionId: string) {
    return computed(() =>
      this.validRecentUsers().filter(u => u.sectionId === sectionId)
    );
  }

  /**
   * Add or update a user in the recent list
   * Called when user detail is opened
   */
  addRecentUser(user: { id: string; name: string; sectionId: string; sex?: string }): void {
    this._recentUsers.update(users => {
      // Remove existing entry if present
      const filtered = users.filter(u => u.id !== user.id);

      // Add new entry at the beginning
      const newEntry: RecentUserEntry = {
        id: user.id,
        name: user.name,
        sectionId: user.sectionId,
        sex: user.sex,
        accessedAt: Date.now(),
      };

      // Limit to MAX_ENTRIES
      const updated = [newEntry, ...filtered].slice(0, MAX_ENTRIES);

      // Persist to localStorage
      this.saveToStorage(updated);

      return updated;
    });
  }

  /**
   * Remove a specific user from recent list
   */
  removeRecentUser(userId: string): void {
    this._recentUsers.update(users => {
      const updated = users.filter(u => u.id !== userId);
      this.saveToStorage(updated);
      return updated;
    });
  }

  /**
   * Clear all recent users
   */
  clearRecentUsers(): void {
    this._recentUsers.set([]);
    this.saveToStorage([]);
  }

  /**
   * Update retention period (in days)
   */
  setRetentionDays(days: number): void {
    if (days > 0 && days <= 365) {
      this._retentionDays.set(days);
    }
  }

  /**
   * Clean up expired entries from storage
   */
  cleanupExpired(): void {
    const validUsers = this.validRecentUsers();
    this._recentUsers.set(validUsers);
    this.saveToStorage(validUsers);
  }

  private loadFromStorage(): RecentUserEntry[] {
    if (!this.isBrowser) return [];

    try {
      const stored = this.document.defaultView?.localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored) as RecentUserEntry[];
      if (!Array.isArray(parsed)) return [];

      // Validate entries have required fields
      return parsed.filter(entry =>
        entry &&
        typeof entry.id === 'string' &&
        typeof entry.name === 'string' &&
        typeof entry.sectionId === 'string' &&
        typeof entry.accessedAt === 'number'
      );
    } catch {
      return [];
    }
  }

  private saveToStorage(users: RecentUserEntry[]): void {
    if (!this.isBrowser) return;

    try {
      this.document.defaultView?.localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
    } catch {
      // localStorage might be full or disabled
      console.warn('Failed to save recent users to localStorage');
    }
  }
}
